import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { getToken } from "@/utils/localstorage";
import { logger } from "@/helpers/tslog";
import { FormatTemplate } from "@/utils/entities";

interface UseFormatTemplatesReturn {
  formatTemplatesRecentlyUsed: FormatTemplate[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  resetSearch: () => void;
  fetchTemplates: (reset?: boolean) => Promise<void>;
}

const useFormatTemplatesRecentlyUsed = (limit: number = 3): UseFormatTemplatesReturn => {
  const [formatTemplatesRecentlyUsed, setFormatTemplatesRecentlyUsed] = useState<FormatTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const fetchTemplates = useCallback(
    async (pageNum: number = 1, reset: boolean = false) => {
      if (isLoading) return;

      setIsLoading(true);
      const apiUrl = process.env.API_URL;
      const token = await getToken();

      try {
        const response = await axios.get(`${apiUrl}/format-template-recently-used`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: pageNum,
            limit: limit,
          },
        });
        console.log(response);

        const newTemplates = response.data;

        if (pageNum === 1 || reset) {
          setFormatTemplatesRecentlyUsed(newTemplates.data);
          setPage(2);
        } else {
          setFormatTemplatesRecentlyUsed((prev) => [...prev, ...newTemplates.data]);
          setPage((prev) => prev + 1);
        }

        setHasMore(newTemplates.pagination.hasMore);
      } catch (e: any) {
        logger.error("Failed to fetch templates:", e);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, limit]
  );

  useEffect(() => {
    fetchTemplates(1, true);
  }, []);

  const loadMore = useCallback(async () => {
    if (!isLoading && hasMore) {
      setIsLoading(true);
      await fetchTemplates(page);
    }
  }, [page, isLoading, hasMore, fetchTemplates]);

  const resetSearch = useCallback(async () => {
    await fetchTemplates(1, true);
  }, [fetchTemplates]);

  return {
    formatTemplatesRecentlyUsed,
    isLoading,
    hasMore,
    loadMore,
    resetSearch,
    fetchTemplates: () => fetchTemplates(1, true),
  };
};

export default useFormatTemplatesRecentlyUsed;
